import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";

// Shape-to-object mapping
const shapeObjectMap = [
    "circle.png", "square.png", "triangle.png", "star.png", "diamond.png", "rectangle.png", "pentagon.png"
];

interface ImagePair {
    left_id: number;
    right_id: number;
    leftObjects: string;
    rightObjects: string;
}

// Function to shuffle an array
const shuffleArray = (array: any[]) => array.sort(() => Math.random() - 0.5);

const MatchShapes: React.FC = () => {
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedShape, setSelectedShape] = useState<{ id: number; x: number; y: number } | null>(null);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [height, setHeight] = useState('0px');
    const modal = useRef<HTMLIonModalElement>(null);

    // Function to generate dynamic questions
    const generateQuestions = () => {
        const shuffledShapes = shuffleArray(shapeObjectMap).slice(0, 4); // Pick 4 random shapes
        const newObjects = shuffledShapes.map((shape, index) => ({
            id: index + 1,
            shape
        }));

        console.log(newObjects)

        const suffledobject = shuffleArray([...newObjects]);
        const newQuestions: ImagePair[] = [];
        newObjects.map((object, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: newObjects.indexOf(suffledobject[index]) + 1,
                leftObjects: object.shape,
                rightObjects: suffledobject[index].shape,
            });
        })

        console.log(newQuestions);

        setQuestions(newQuestions);
        setConnections([]);
        setSelectedShape(null);
    };

    useEffect(() => {
        generateQuestions();
        document.title = "Match Shapes Worksheet for LKG | Shape Recognition Activities | Mama’s Guidance"; // Set page title
    }, []);

    // Get position relative to container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect();
        setHeight(container.scrollHeight + 'px');

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle shape click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedShape({ id, x: position.x, y: position.y });
    };

    // Handle object click
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedShape) {
            const position = getRelativePosition(event.currentTarget);
            const isCorrect = selectedShape.id === rightId;

            setConnections([...connections, { x1: selectedShape.x, y1: selectedShape.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedShape(null);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Enhance shape recognition in LKG children with interactive Match the Shapes activities on Mama’s Guidance—designed to boost visual discrimination and early geometry skills through fun and engaging exercises.' />
            </Helmet>
            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
            <IonModal
                ref={modal}
                trigger="open-modal"
                initialBreakpoint={1}
                breakpoints={[0, 0.5, 0.75, 1]}
                handleBehavior="cycle"
                backdropDismiss={false}  // ✅ Prevents click-to-close
                className="full-page-modal"
            >
                <IonContent className="ion-padding">
                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                    <h1>Match Shapes</h1>

                    <p>
                        Matching shapes is one of the most engaging and visual ways to teach preschoolers about geometry. In this worksheet, <strong>LKG children</strong> learn to match shapes like <strong>circle, triangle, square, and rectangle</strong> with similar outlines or familiar objects. It develops both <strong>visual memory</strong> and <strong>early math skills</strong>.
                    </p>

                    <h2>🔷 Why Shape Matching Is Important</h2>
                    <ul>
                        <li>Improves visual discrimination and cognitive development</li>
                        <li>Helps children recognize patterns and geometric forms</li>
                        <li>Reinforces shape names through hands-on interaction</li>
                        <li>Builds pre-writing and classification skills</li>
                    </ul>

                    <h2>🎯 Learning Objectives</h2>
                    <ul>
                        <li>Identify and name basic 2D shapes</li>
                        <li>Match shapes with outlines or corresponding real-world objects</li>
                        <li>Recognize differences between similar shapes</li>
                        <li>Develop logical thinking and hand-eye coordination</li>
                    </ul>

                    <h2>📋 What’s Inside the Worksheet?</h2>
                    <ul>
                        <li>Match each shape to its pair (<strong>circle to circle, square to square, etc.</strong>)</li>
                        <li>Match a shape with a real-life object (<strong>e.g., circle → clock</strong>)</li>
                        <li>Color the matching shapes in the same color</li>
                        <li>Simple drag-and-match or line-drawing activities</li>
                    </ul>

                    <h2>👩‍🏫 Teaching Tips for Parents</h2>
                    <ul>
                        <li>Cut out shape flashcards and ask your child to find matching items in the house</li>
                        <li>Play matching games on paper or mobile apps</li>
                        <li>Encourage your child to say the name of each shape as they match</li>
                        <li>Use building blocks and sort them by shapes</li>
                    </ul>


                    <h2>🔗 Related Topics</h2>
                    <div>
                        <IonItem routerLink="/lkg/mathematics/missing-number"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Missing Number</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Before / After / Between Number</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/lkg/mathematics/skip-number"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                        </IonItem>
                    </div>

                    <h2>🌟 Mama’s Guidance Tip</h2>
                    <p>
                        Turn everyday learning into a game — <strong>“Can you find something in this room that looks like a triangle?”</strong> Real-life shape matching builds curiosity and memory!
                    </p>

                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                </IonContent>
            </IonModal>
            <article className="visually-hidden">
                <h1>Match Shapes</h1>

                <p>
                    Matching shapes is one of the most engaging and visual ways to teach preschoolers about geometry. In this worksheet, <strong>LKG children</strong> learn to match shapes like <strong>circle, triangle, square, and rectangle</strong> with similar outlines or familiar objects. It develops both <strong>visual memory</strong> and <strong>early math skills</strong>.
                </p>

                <h2>🔷 Why Shape Matching Is Important</h2>
                <ul>
                    <li>Improves visual discrimination and cognitive development</li>
                    <li>Helps children recognize patterns and geometric forms</li>
                    <li>Reinforces shape names through hands-on interaction</li>
                    <li>Builds pre-writing and classification skills</li>
                </ul>

                <h2>🎯 Learning Objectives</h2>
                <ul>
                    <li>Identify and name basic 2D shapes</li>
                    <li>Match shapes with outlines or corresponding real-world objects</li>
                    <li>Recognize differences between similar shapes</li>
                    <li>Develop logical thinking and hand-eye coordination</li>
                </ul>

                <h2>📋 What’s Inside the Worksheet?</h2>
                <ul>
                    <li>Match each shape to its pair (<strong>circle to circle, square to square, etc.</strong>)</li>
                    <li>Match a shape with a real-life object (<strong>e.g., circle → clock</strong>)</li>
                    <li>Color the matching shapes in the same color</li>
                    <li>Simple drag-and-match or line-drawing activities</li>
                </ul>

                <h2>👩‍🏫 Teaching Tips for Parents</h2>
                <ul>
                    <li>Cut out shape flashcards and ask your child to find matching items in the house</li>
                    <li>Play matching games on paper or mobile apps</li>
                    <li>Encourage your child to say the name of each shape as they match</li>
                    <li>Use building blocks and sort them by shapes</li>
                </ul>


                <h2>🔗 Related Topics</h2>
                <div>
                    <IonItem routerLink="/lkg/mathematics/missing-number"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Missing Number</IonLabel>
                    </IonItem>
                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Before / After / Between Number</IonLabel>
                    </IonItem>
                    <IonItem routerLink="/lkg/mathematics/skip-number"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Skip Number by 2 or 5</IonLabel>
                    </IonItem>
                </div>

                <h2>🌟 Mama’s Guidance Tip</h2>
                <p>
                    Turn everyday learning into a game — <strong>“Can you find something in this room that looks like a triangle?”</strong> Real-life shape matching builds curiosity and memory!
                </p>
            </article>
            <div ref={containerRef} className="contentPart additionWord matchObject match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                {/* SVG for connections */}
                <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                    {connections.map((conn, index) => (
                        <line key={index} x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} stroke={conn.correct ? "green" : "red"} strokeWidth="3" />
                    ))}
                </svg>


                <div className=" columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%", height: "100%" }}>
                    {/* Left Column - Shapes */}
                    <div className=" columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                        <div className="left-column">
                            {questions.map((item) => (
                                <div key={item.left_id} className={`image-box ${selectedShape?.id === item.left_id ? "selected" : ""}`} onClick={(e) => handleLeftClick(e, item.left_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                    <img src={process.env.REACT_APP_API_URL + item.leftObjects} />
                                </div>
                            ))}
                        </div>
                        <div className="right-column">
                            {questions.map((item) => (
                                <div key={item.right_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                    <img src={process.env.REACT_APP_API_URL + item.rightObjects} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="mt-3 text-center">
                    <button className="btn hero-btn" onClick={generateQuestions}>Next Question</button>
                </div>
            </div>
        </>
    );
};

export default MatchShapes;
